import { useEffect, useState, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, Plus, Trash2, ChevronRight, Users, Calendar } from 'lucide-react'
import { shoppingApi, type ShoppingList } from '../services/api'
import { useAuth } from '../contexts/useAuth'
import toast from 'react-hot-toast'

export default function Home() {
    const [lists, setLists] = useState<ShoppingList[]>([])
    const [loading, setLoading] = useState(true)
    const { isAuthenticated, loading: authLoading } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (authLoading) return

        let active = true
        Promise.resolve().then(() => {
            if (active) setLoading(true)
        })
        shoppingApi.list()
            .then(res => {
                if (active) setLists(res)
            })
            .catch(() => {
                if (active) toast.error('Impossible de charger les listes')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [isAuthenticated, authLoading])

    const handleDelete = async (id: string, e: MouseEvent) => {
        e.stopPropagation()
        if (!confirm('Supprimer cette liste ?')) return
        await shoppingApi.delete(id)
        setLists(prev => prev.filter(l => l.id !== id))
        toast.success('Liste supprimée')
    }

    const statusLabel: Record<string, { label: string; cls: string }> = {
        draft: { label: 'Brouillon', cls: 'badge-gray' },
        shopping: { label: 'En cours', cls: 'badge-warning' },
        done: { label: 'Terminée', cls: 'badge-success' },
    }

    return (
        <div>
            {/* Hero */}
            <div style={{ padding: '40px 0 20px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-3)', fontSize: '0.82rem', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Bienvenue sur</p>
                <h1 style={{ fontSize: '2.8rem', marginBottom: 10, lineHeight: 1.1 }}>
                    <span className="hero-gradient">EasyMarket</span>
                </h1>
                <p style={{ color: 'var(--text-2)', fontSize: '1rem', maxWidth: '85%', margin: '0 auto', lineHeight: 1.6 }}>
                    Votre assistant de courses intelligent et minimaliste.
                </p>
            </div>

            {/* Quick action */}
            <button
                className="btn btn-primary btn-full"
                style={{ marginTop: 24, padding: '16px 20px', fontSize: '0.95rem' }}
                onClick={() => navigate('/generate')}
            >
                <Plus size={20} />
                Générer une nouvelle liste
            </button>

            {/* Lists */}
            <p className="section-title" style={{ marginTop: 36 }}>Mes listes ({lists.length})</p>

            {loading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[1, 2].map(i => <div key={i} className="skeleton" style={{ height: 84, borderRadius: 16 }} />)}
                </div>
            )}

            {!loading && lists.length === 0 && (
                <div className="empty-state">
                    <ShoppingCart size={48} />
                    <h3>Aucune liste</h3>
                    <p>Commencez par générer votre première liste de courses</p>
                </div>
            )}

            {!loading && lists.map((list, idx) => {
                const st = statusLabel[list.status] || statusLabel.draft
                const toGet = list.items.filter(i => !i.owned).length
                return (
                    <div
                        key={list.id}
                        className="card card-clickable"
                        style={{
                            marginBottom: 10,
                            animation: `slideUp 0.4s ease ${idx * 0.06}s both`,
                        }}
                        onClick={() => navigate(`/shopping/${list.id}`)}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <h3 style={{ fontSize: '1rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.name}</h3>
                                    <span className={`badge ${st.cls}`}>{st.label}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 14, color: 'var(--text-3)', fontSize: '0.8rem' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Users size={13} /> {list.nb_persons} p.
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Calendar size={13} /> {list.duration_days} j.
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <ShoppingCart size={13} /> {toGet} article{toGet > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                <button className="btn btn-icon btn-danger btn-sm" onClick={e => handleDelete(list.id, e)}>
                                    <Trash2 size={14} />
                                </button>
                                <ChevronRight size={18} color="var(--text-3)" />
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
